import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Sparkles, ChevronLeft, Plus, Search, Trash2, Edit, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CATEGORIES = [
  'account', 'community', 'maintenance', 'documents', 'communication',
  'voting', 'finance', 'mobile', 'admin', 'agent', 'governance', 'security',
];
const AUDIENCES = ['resident', 'trustee', 'managing_agent', 'platform_owner'];

const EMPTY = {
  kb_id: null, category: 'account', title: '', body: '',
  keywords: [], audience: ['resident', 'trustee', 'managing_agent', 'platform_owner'],
};

export default function BoDyKnowledge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchArticles = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (category) params.set('category', category);
    const r = await fetch(`${API}/api/bo-dy/kb?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setArticles(d.articles || []);
    }
  }, [search, category]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${API}/api/bo-dy/kb/seed`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      const d = await r.json();
      toast.success(`Seeded ${d.inserted} new + ${d.updated} updated (${d.total_in_seed} in seed)`);
      fetchArticles();
    } catch (e) {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const save = async () => {
    if (!editing.title.trim() || !editing.body.trim()) {
      return toast.error('Title and body are required');
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/bo-dy/kb`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(editing),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Article saved');
      setEditing(null);
      fetchArticles();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kb_id) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    const r = await fetch(`${API}/api/bo-dy/kb/${kb_id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      toast.success('Deleted');
      fetchArticles();
    } else {
      toast.error('Delete failed');
    }
  };

  const addKeyword = () => {
    if (!keywordInput.trim()) return;
    setEditing({ ...editing, keywords: [...new Set([...(editing.keywords || []), keywordInput.trim().toLowerCase()])] });
    setKeywordInput('');
  };
  const removeKeyword = (k) => {
    setEditing({ ...editing, keywords: editing.keywords.filter((x) => x !== k) });
  };
  const toggleAudience = (a) => {
    setEditing({
      ...editing,
      audience: editing.audience.includes(a)
        ? editing.audience.filter((x) => x !== a)
        : [...editing.audience, a],
    });
  };

  if (!user || user.role !== 'platform_owner') {
    return <div className="p-8 text-center text-gray-500">Platform Owner access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="bo-dy-knowledge-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate('/platform')} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-indigo-600" /> Bo-Dy Knowledge Base
            </h1>
            <p className="text-gray-600 text-sm">FAQ articles Bo-Dy will reference when answering users.</p>
          </div>
          <Button variant="outline" onClick={seed} disabled={seeding} data-testid="seed-btn">
            <Download className="w-4 h-4 mr-2" />
            {seeding ? 'Seeding…' : 'Seed Starter Articles'}
          </Button>
          <Button onClick={() => setEditing({ ...EMPTY })} data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-2" /> New Article
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4 flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title or body…"
                className="pl-9"
                data-testid="kb-search"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2 border rounded"
              data-testid="kb-category-filter"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </CardContent>
        </Card>

        {/* Article list */}
        {articles.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No articles yet. Click <strong>Seed Starter Articles</strong> to bootstrap 30 FAQs.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {articles.map((a) => (
              <Card key={a.kb_id} data-testid={`kb-row-${a.kb_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className="capitalize bg-indigo-50 text-indigo-700 border-indigo-200">{a.category}</Badge>
                        <span className="font-semibold">{a.title}</span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{a.body}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(a.keywords || []).slice(0, 8).map((k) => (
                          <Badge key={k} variant="outline" className="text-[10px] bg-slate-50">{k}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => setEditing({ ...a })} data-testid={`edit-${a.kb_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(a.kb_id)} className="text-red-600" data-testid={`delete-${a.kb_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kb-editor">
          <DialogHeader>
            <DialogTitle>{editing?.kb_id ? 'Edit Article' : 'New Article'}</DialogTitle>
            <DialogDescription>Bo-Dy will use these when answering matching questions.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="kb-title" />
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  data-testid="kb-category"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  rows={6}
                  placeholder="Use clear, simple language. Numbered steps for troubleshooting."
                  data-testid="kb-body"
                />
              </div>
              <div>
                <Label>Keywords (boost relevance)</Label>
                <div className="flex gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Type a keyword and press Enter"
                    data-testid="kb-keyword-input"
                  />
                  <Button type="button" onClick={addKeyword}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {editing.keywords.map((k) => (
                    <Badge key={k} className="bg-indigo-100 text-indigo-700 cursor-pointer" onClick={() => removeKeyword(k)}>
                      {k} ×
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>Audience</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {AUDIENCES.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggleAudience(a)}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        editing.audience.includes(a)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-600 border-slate-300'
                      }`}
                      data-testid={`kb-audience-${a}`}
                    >
                      {a.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="kb-save">
              {saving ? 'Saving…' : 'Save Article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
